import React from 'react';
import {
  Box,
  Text,
  UnorderedList,
  ListItem,
  Link,
  Stack,
} from '@chakra-ui/react';
import Heading from '#coreComponents/Heading';

export const faqs: { question: string; answer: React.ReactNode }[] = [
  {
    question: 'What tools and data can I access through THE DataPoints?',
    answer: (
      <>
        <Text>
          THE DataPoints provides access to tools and data to understand your
          institution’s individual performance based on THE World University
          Rankings and other data, and to allow you to understand your relative
          performance based on a comparison with your peers or benchmark sets.
        </Text>
        <Text>
          THE WUR Dashboard is a toolset that focuses on THE World University
          Rankings. It illuminates performance patterns within the sector,
          giving you insight into potential collaborators or competitors by
          investigating the actual performance on research, teaching, knowledge
          transfer and internationalisation metrics in a global context.
        </Text>
        <Text>
          The data provided reflects both the data published on the THE website,
          but also more detailed data examining each of the metrics that
          underpin performance.
        </Text>
      </>
    ),
  },
  {
    question:
      'How does Times Higher Education compile the data behind THE DataPoints?',
    answer: (
      <Text>
        In 2026, the data for THE WUR Dashboard was drawn from on information
        from over 3,100 institutions, 188.7 million citations and 1 million
        publications and over 69,000 academic survey responses that form the
        backbone of the prestigious World University Rankings. This data is
        submitted via our portal, through the Times Higher Education reputation
        survey, and the Scopus dataset provided by Elsevier. We clean and
        transform this data to create the 17 metrics that feed into the 5
        pillars of the rankings to give you an accurate and credible picture of
        your global performance.
      </Text>
    ),
  },
  {
    question:
      'What breadth of the global higher education market is covered by THE DataPoints?',
    answer: (
      <Text>
        Times Higher Education has expanded its data coverage to include over
        3,100 institutions – including reporters - for the World University
        Rankings 2027 (published in September 2026). We are committed to
        increasing our global view year on year to ensure our data products
        remain the most complete in the world.
      </Text>
    ),
  },
  {
    question: 'How many benchmark institutions can I select?',
    answer: (
      <Text>
        The quantity varies with your subscription package, from none up to 50.
        We can also help you in choosing a benchmark group that is relevant to
        your needs.
      </Text>
    ),
  },
  {
    question: 'Can I download data from THE WUR Dashboard?',
    answer: (
      <Text>
        All data included in your subscription is available to download to
        Microsoft Excel from THE DataPoints.
      </Text>
    ),
  },
  {
    question:
      'How can I use THE WUR Dashboard to assess the differences between my university and my competitors?',
    answer: (
      <Text>
        As part of a THE WUR Dashboard subscription we make available scores and
        values for both your university and those of your selected peers across
        the 5 pillars and the 17 metrics of the THE World University Rankings.
        With these you can assess the actual difference, not only the distance
        in scores, in each individual metric, between yourselves and your peers.
        The data is provided graphically, where appropriate, as well as in
        tabular form, and can also be downloaded into Microsoft Excel format for
        your own analysis.
      </Text>
    ),
  },
  {
    question:
      'How is the similarity of my university to my selected peers determined?',
    answer: (
      <Text>
        When we use <Text as="em">Similarity</Text> in the THE WUR Dashboard we
        calculate it directly from the difference between two scores. A lower
        distance means higher similarity.
      </Text>
    ),
  },
  {
    question:
      'What is meant by “user defined groups” and how are they determined?',
    answer: (
      <Text>
        The user defined groups are universities that themselves nominate to
        help them understand their performance relative to their peers. However,
        unlike the benchmark institutions, the university would not see the
        individual performance data of the members of the target and stretch
        groups. Instead they will see the distribution of the performance data
        of the groups e.g. 25% percentile, median, 75% percentile etc. Clients
        can either choose pre-defined groupings such as the Association of
        American University, Russell Group etc, or they can hand pick their own
        groups.
      </Text>
    ),
  },
  {
    question: 'How many users can access my subscription?',
    answer: (
      <Text>
        Again, the number of logins available for your institution will vary
        according to your subscription level. We can help you think about the
        number of logins that would be sensible for your purposes.
      </Text>
    ),
  },
  {
    question: 'How is the citation data normalised by subject-area?',
    answer: (
      <Text>
        In each of the 334 detailed subjects in Scopus, we calculate the average
        number of citations across the time period (for the relevant paper
        types). We find each paper that any academic affiliated with university
        X has written in the time period. For each paper we calculate how the
        number of citations compares to the average number in that subject. For
        example if the average number is 20 and this paper has 10 then it scores
        0.5. If it has 25 then it scores 1.25. We average the scores for every
        paper across the whole university.
      </Text>
    ),
  },
];

const Faqs = () => (
  <Box>
    <Heading as="h1" mb={4}>
      Frequently Asked Questions
    </Heading>
    <Heading as="h2" fontSize="xl" mb={4}>
      These Frequently Asked Questions apply to Times Higher Education’s
      flagship performance analysis and benchmarking platform, THE DataPoints.
    </Heading>
    <Text>
      For information on our university rankings methodologies, please see the
      Times Higher Education website:
    </Text>
    <UnorderedList mt={2} spacing={2}>
      <ListItem>
        <Link
          color="yellow.500"
          href="https://www.timeshighereducation.com/world-university-rankings/methodology"
          target="_blank"
        >
          Times Higher Education World University Rankings Methodology
        </Link>
      </ListItem>
    </UnorderedList>
    <Stack my={6} spacing={4}>
      {faqs.map(({ question, answer }) => (
        <Box key={question}>
          <Heading as="h2" fontSize="xl" mb={2}>
            {question}
          </Heading>
          <Box>{answer}</Box>
        </Box>
      ))}
    </Stack>
  </Box>
);
export default Faqs;
